"use client";
import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import Image from "next/image";
import { IMAGES } from "../constants/images";
import { useTranslation } from "react-i18next";

const ZaynFeatureComparison = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const [isClient, setIsClient] = useState(false);

  const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const isXs = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });

  useEffect(() => {
    setIsClient(true);
  }, []);

  const featuresRaw = t("zaynFeatureComparison.features", {
    returnObjects: true,
  });
  const zaynFeaturesRaw = t("zaynFeatureComparison.zaynFeatures", {
    returnObjects: true,
  });
  const humanWithoutZaynRaw = t(
    "zaynFeatureComparison.humanWithoutZaynFeatures",
    { returnObjects: true },
  );
  const otherToolsRaw = t("zaynFeatureComparison.otherToolsFeatures", {
    returnObjects: true,
  });

  const features = Array.isArray(featuresRaw) ? featuresRaw : [];
  const zaynFeatures = Array.isArray(zaynFeaturesRaw) ? zaynFeaturesRaw : [];
  const humanWithoutZayn = Array.isArray(humanWithoutZaynRaw)
    ? humanWithoutZaynRaw
    : [];
  const otherTools = Array.isArray(otherToolsRaw) ? otherToolsRaw : [];

  return (
    <Box
      suppressHydrationWarning={true}
      sx={{
        backgroundImage: `url('${IMAGES.FEATURE_COMPARISON_BG}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        py: { xs: 3, sm: 4, md: 6, lg: 10 },
        px: { xs: 0, sm: 3, md: 0 },
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="xl" sx={{ position: "relative" }}>
        {/* Header */}
        <Box
          textAlign={
            isClient && isXs ? (isArabic ? "right" : "left") : "center"
          }
          mb={{ xs: 4, sm: 5, md: 8 }}
          sx={{ ml: isClient && isXs ? 1 : 0, mr: isClient && isXs ? 1 : 0 }}
        >
          <Typography
            variant={isClient && isMobile ? "h5" : "h3"}
            component="h2"
            sx={{
              color: "#020617",
              fontWeight: 600,
              mb: 0.5,
              lineHeight: 1.3,
              fontSize: {
                xs: "1.5rem",
                sm: "1.75rem",
                md: "2rem",
                lg: "2.1rem",
                xl: "2.5rem",
              },
              fontFamily: "var(--font-plusjakarta)",
            }}
          >
            {t("zaynFeatureComparison.heading1")}{" "}
            <Box component="span" sx={{ color: "#9048FB" }}>
              {t("zaynFeatureComparison.headingHighlight1")}
            </Box>{" "}
            {t("zaynFeatureComparison.heading1End")}
          </Typography>
          <Typography
            variant={isClient && isMobile ? "h6" : "h4"}
            component="h2"
            sx={{
              color: "#020617",
              fontWeight: 600,
              lineHeight: 1.3,
              fontSize: {
                xs: "1.5rem",
                sm: "1.75rem",
                md: "2rem",
                lg: "2.1rem",
                xl: "2.5rem",
              },
              fontFamily: "var(--font-plusjakarta)",
            }}
          >
            {t("zaynFeatureComparison.heading2")}{" "}
            <Box component="span" sx={{ color: "#9048FB" }}>
              {t("zaynFeatureComparison.headingHighlight2")}
            </Box>{" "}
            {t("zaynFeatureComparison.heading2End")}
          </Typography>
        </Box>

        {/* Comparison Table */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "stretch",
            gap: 0,
            mt: { xs: 2, sm: 4, md: 8, lg: 20 },
            position: "relative",
            mx: "auto",
          }}
        >
          {/* Human Without Zayn Heading */}
          <Box
            textAlign="center"
            mb={{ xs: 1.5, sm: 2, md: 2.5, lg: 3 }}
            sx={{
              top: "-21%",
              right: isArabic
                ? "unset"
                : {
                    xs: "30.1%",
                    sm: "30.1%",
                    md: "30.1%",
                    lg: "26%",
                    xl: "30.1%",
                  },
              left: isArabic
                ? {
                    xs: "30.1%",
                    sm: "30.1%",
                    md: "30.1%",
                    lg: "26%",
                    xl: "30.1%",
                  }
                : "unset",
              position: "absolute",
              height: { xs: "60px", sm: "70px", md: "90px", lg: "105px" },
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#6B7280",
                fontSize: {
                  xs: "0.75rem",
                  sm: "0.85rem",
                  md: "1rem",
                  lg: "1rem",
                },
                fontFamily: "var(--font-outfit)",
              }}
            >
              {t("zaynFeatureComparison.humanWithoutZaynFull")}
            </Typography>
          </Box>

          {/* Other Tools Heading */}
          <Box
            textAlign="center"
            mb={{ xs: 1.5, sm: 2, md: 2.5, lg: 3 }}
            sx={{
              top: "-21%",
              right: isArabic
                ? "unset"
                : { xs: "20%", sm: "20%", md: "20%", lg: "15%", xl: "20%" },
              left: isArabic
                ? { xs: "20%", sm: "20%", md: "20%", lg: "15%", xl: "20%" }
                : "unset",
              position: "absolute",
              height: { xs: "60px", sm: "70px", md: "90px", lg: "105px" },
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#6B7280",
                fontSize: {
                  xs: "0.75rem",
                  sm: "0.85rem",
                  md: "1rem",
                  lg: "1rem",
                },
                fontFamily: "var(--font-outfit)",
              }}
            >
              {t("zaynFeatureComparison.otherTools")}
            </Typography>
          </Box>

          {/* Features List Card */}
          <Card
            sx={{
              width: { xs: "45%", sm: "50%", md: "350px" },
              minWidth: { xs: "140px", sm: "160px" },
              borderRadius: isArabic
                ? { xs: "0 16px 16px 0", sm: "0 18px 18px 0", md: "24px" }
                : { xs: "16px 0 0 16px", sm: "18px 0 0 18px", md: "24px" },
              background: "rgba(255,255,255,0.98)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.3)",
              display: "flex",
              flexDirection: "column",
              pt: { xs: 3.5, md: 0 },
              mr: isArabic ? 0 : { xs: 0, sm: 0, md: -5 },
              ml: isArabic ? { xs: 0, sm: 0, md: -5 } : 0,
              boxShadow: "none",
            }}
          >
            <CardContent
              sx={{
                p: { xs: 1.5, sm: 2, md: 3, lg: 4 },
                display: "flex",
                flexDirection: "column",
                flex: 1,
              }}
            >
              <Stack spacing={{ xs: 1.5, sm: 1.6, md: 2, lg: 2.3 }}>
                {features.map((feature, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      minHeight: {
                        xs: "28px",
                        sm: "30px",
                        md: "36px",
                        lg: "40px",
                      },
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#3D4E5C",
                        fontWeight: 400,
                        fontSize: {
                          xs: "0.65rem",
                          sm: "0.7rem",
                          md: "0.85rem",
                          lg: "1rem",
                        },
                        fontFamily: "var(--font-outfit)",
                        ml: isArabic ? 0 : { xs: "4%", sm: "3%", md: "3%" },
                        mr: isArabic ? { xs: "4%", sm: "3%", md: "3%" } : 0,
                        textAlign: isArabic ? "right" : "left",
                      }}
                    >
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>

          {/* Zayn Column */}
          <Card
            sx={{
              width: { xs: "27.5%", sm: "25%", md: "330px" },
              minWidth: { xs: "80px", sm: "90px" },
              borderRadius: { xs: "0", md: "24px" },
              background: "#C285FF",
              color: "white",
              position: "relative",
              zIndex: 2,
              display: "flex",
              flexDirection: "column",
              mb: { xs: 0, md: -6 },
              mt: { xs: 0, md: -14 },
            }}
          >
            <CardContent
              sx={{
                p: { xs: 1.2, sm: 1.5, md: 2.5, lg: 4 },
                display: "flex",
                flexDirection: "column",
                flex: 1,
              }}
            >
              <Box
                textAlign="center"
                mb={{ xs: 2, sm: 2, md: 2.5, lg: 3 }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#fff",
                    fontSize: {
                      xs: "0.6rem",
                      sm: "0.85rem",
                      md: "1rem",
                      lg: "1.1rem",
                    },
                    fontFamily: "var(--font-outfit)",
                  }}
                >
                  {isClient && isXs
                    ? t("zaynFeatureComparison.withZaynShort")
                    : t("zaynFeatureComparison.withZaynFull")}
                </Typography>
              </Box>

              <Stack
                mt={{ xs: 0, md: 8 }}
                spacing={{ xs: 1.5, sm: 1.6, md: 2, lg: 2.3 }}
              >
                {zaynFeatures.map((feature, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      justifyContent: {
                        xs: "center",
                        md: isArabic ? "flex-end" : "flex-start",
                      },
                      alignItems: "center",
                      flexDirection: isArabic ? "row-reverse" : "row",
                      gap: { xs: 0.5, sm: 0.8, md: 1 },
                      minHeight: {
                        xs: "28px",
                        sm: "30px",
                        md: "36px",
                        lg: "40px",
                      },
                      pl: isArabic ? 0 : { xs: 0, sm: 0, md: 1 },
                      pr: isArabic ? { xs: 0, sm: 0, md: 1 } : 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 14, sm: 16, md: 18 },
                        height: { xs: 14, sm: 16, md: 18 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Image
                        src={IMAGES.TICK_ICON}
                        alt="Check"
                        width={18}
                        height={18}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#fff",
                        fontWeight: 500,
                        fontSize: {
                          xs: "0.55rem",
                          sm: "0.6rem",
                          md: "0.75rem",
                          lg: "0.9rem",
                        },
                        fontFamily: "var(--font-outfit)",
                        textAlign: isArabic ? "right" : "left",
                        display: { xs: "none", md: "block" },
                      }}
                    >
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>

          {/* Human Without Zayn Column */}
          <Card
            sx={{
              width: { xs: "27.5%", sm: "25%", md: "300px" },
              minWidth: { xs: "80px", sm: "90px" },
              borderRadius: {
                xs: "0 16px 16px 0",
                sm: "0 18px 18px 0",
                md: "0 24px 24px 0",
              },
              background: "rgba(255,255,255,0.98)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.3)",
              borderLeft: "none",
              boxShadow: "none",
              ml: { xs: -1.5, sm: -2 },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent
              sx={{
                p: { xs: 1.5, sm: 2, md: 2.5, lg: 4 },
                display: "flex",
                flexDirection: "column",
                flex: 1,
              }}
            >
              <Box
                textAlign="center"
                mb={{ xs: 2, sm: 2, md: 2.5, lg: 3 }}
                sx={{
                  height: "auto",
                  display: { xs: "flex", md: "none" },
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#6B7280",
                    fontSize: {
                      xs: "0.5rem",
                      sm: "0.85rem",
                      md: "1rem",
                      lg: "1.1rem",
                    },
                    fontFamily: "var(--font-outfit)",
                  }}
                >
                  {t("zaynFeatureComparison.withoutZayn")}
                </Typography>
              </Box>

              <Stack spacing={{ xs: 1.5, sm: 1.6, md: 2, lg: 2.3 }}>
                {humanWithoutZayn.map((feature, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      justifyContent: {
                        xs: "center",
                        md: isArabic ? "flex-end" : "flex-start",
                      },
                      alignItems: "center",
                      flexDirection: isArabic ? "row-reverse" : "row",
                      gap: { xs: 0.5, sm: 0.8, md: 1 },
                      minHeight: {
                        xs: "28px",
                        sm: "30px",
                        md: "36px",
                        lg: "40px",
                      },
                      pl: isArabic ? 0 : { xs: 0, sm: 0, md: 1 },
                      pr: isArabic ? { xs: 0, sm: 0, md: 1 } : 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 14, sm: 16, md: 18 },
                        height: { xs: 14, sm: 16, md: 18 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Image
                        src={IMAGES.CROSS_ICON}
                        alt="Cross"
                        width={18}
                        height={18}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#3D4E5C",
                        fontWeight: 400,
                        fontSize: {
                          xs: "0.55rem",
                          sm: "0.6rem",
                          md: "0.75rem",
                          lg: "0.9rem",
                        },
                        fontFamily: "var(--font-outfit)",
                        textAlign: isArabic ? "right" : "left",
                        display: { xs: "none", md: "block" },
                      }}
                    >
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>

          {/* Other Tools Column */}
          <Card
            sx={{
              width: { xs: "27.5%", sm: "25%", md: "300px" },
              minWidth: { xs: "80px", sm: "90px" },
              borderRadius: {
                xs: "0 16px 16px 0",
                sm: "0 18px 18px 0",
                md: "0 24px 24px 0",
              },
              background: "rgba(255,255,255,0.98)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.3)",
              borderLeft: "none",
              ml: { xs: -1.5, sm: -2 },
              display: "flex",
              flexDirection: "column",
              boxShadow: "none",
            }}
          >
            <CardContent
              sx={{
                p: { xs: 1.5, sm: 2, md: 2.5, lg: 4 },
                display: "flex",
                flexDirection: "column",
                flex: 1,
              }}
            >
              <Box
                textAlign="center"
                mb={{ xs: 2, sm: 2, md: 2.5, lg: 3 }}
                sx={{
                  height: "auto",
                  display: { xs: "flex", md: "none" },
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#6B7280",
                    fontSize: {
                      xs: "0.5rem",
                      sm: "0.85rem",
                      md: "1rem",
                      lg: "1.1rem",
                    },
                    fontFamily: "var(--font-outfit)",
                  }}
                >
                  {t("zaynFeatureComparison.otherTools")}
                </Typography>
              </Box>

              <Stack spacing={{ xs: 1.5, sm: 1.6, md: 2, lg: 2.3 }}>
                {otherTools.map((feature, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      justifyContent: {
                        xs: "center",
                        md: isArabic ? "flex-end" : "flex-start",
                      },
                      alignItems: "center",
                      flexDirection: isArabic ? "row-reverse" : "row",
                      gap: { xs: 0.5, sm: 0.8, md: 1 },
                      minHeight: {
                        xs: "28px",
                        sm: "30px",
                        md: "36px",
                        lg: "40px",
                      },
                      pl: isArabic ? 0 : { xs: 0, sm: 0, md: 1 },
                      pr: isArabic ? { xs: 0, sm: 0, md: 1 } : 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 14, sm: 16, md: 18 },
                        height: { xs: 14, sm: 16, md: 18 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Image
                        src={IMAGES.CROSS_ICON}
                        alt="Cross"
                        width={18}
                        height={18}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#3D4E5C",
                        fontWeight: 400,
                        fontSize: {
                          xs: "0.55rem",
                          sm: "0.6rem",
                          md: "0.75rem",
                          lg: "0.9rem",
                        },
                        fontFamily: "var(--font-outfit)",
                        textAlign: isArabic ? "right" : "left",
                        display: { xs: "none", md: "block" },
                      }}
                    >
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Box>
  );
};

export default ZaynFeatureComparison;
